import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useRoutes } from 'react-router-dom';
import router from 'src/router';
import './index.css';
import './assets/css/home.css';
import './assets/css/footer.css';
import './assets/css/navbar.css';
import './assets/css/signin.css';
import './assets/css/signup.css';
import './assets/css/motionSlider.css';
import { useTranslation } from 'react-i18next';

import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { CssBaseline, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ThemeProvider from './theme/ThemeProvider';
import { useSelector } from 'react-redux';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { initSounds } from './utils/sound';

// ✅ Pages publiques : layout Bootstrap doit TOUJOURS rester LTR
const PUBLIC_LTR_PAGES = ['/', '/Signup', '/ForgotPassword', '/ResetPassword', '/auth/google/success', '/auth/facebook/success'];
const isPublicLTRPage = (pathname) =>
  PUBLIC_LTR_PAGES.includes(pathname) || pathname.startsWith('/ResetPassword');

// ✅ FIX CRITIQUE : forcer ltr IMMÉDIATEMENT avant que React se monte
// Cela évite le "flash" RTL visible au chargement quand ar est en localStorage
if (isPublicLTRPage(window.location.pathname)) {
  document.documentElement.dir = 'ltr';
}

// ---------------- ProtectedRoute ----------------
function ProtectedRoute({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const authUser = useSelector((state) => state.client || {});

  // ← Lire aussi depuis localStorage comme fallback
  const accessToken = authUser?.accessToken || localStorage.getItem('accessToken');

  const PUBLIC = new Set([
    '/', '/about', '/terms', '/privacy',
    '/Signup', '/ForgotPassword', '/ResetPassword',
    '/auth/google/success',
    '/signin',
    '/auth/facebook/success',
  ]);

  const isPublicPage =
    PUBLIC.has(location.pathname) ||
    location.pathname.startsWith('/ResetPassword');

  useEffect(() => {
    if (!accessToken && !isPublicPage) {
      navigate('/', { replace: true });
      return;
    }
    if (accessToken && location.pathname === '/') {
      navigate('/dashboards/home', { replace: true });
    }
  }, [accessToken, isPublicPage, location.pathname, navigate]);

  if (!accessToken && !isPublicPage) return null;

  return children;
}
// ---------------- AppInner ----------------
function AppInner() {
  const content = useRoutes(router);
  const location = useLocation();
  const { i18n } = useTranslation();
  const [direction, setDirection] = useState('ltr');

  useEffect(() => {
    initSounds();
  }, []);

  useEffect(() => {
    const applyDirection = (lng) => {
      const isRTLLang = lng === 'ar';
      const isPublic = isPublicLTRPage(location.pathname);

      if (isPublic) {
        // ✅ Pages login/signup : TOUJOURS ltr sur <html>
        // Le RTL des textes arabes est géré par className="rtl" dans SignIn.jsx
        document.documentElement.dir = 'ltr';
        setDirection('ltr');
      } else {
        // ✅ Pages dashboard : RTL normal pour l'arabe
        document.documentElement.dir = isRTLLang ? 'rtl' : 'ltr';
        setDirection(isRTLLang ? 'rtl' : 'ltr');
      }
    };

    applyDirection(i18n.language);
    i18n.on('languageChanged', applyDirection);
    return () => i18n.off('languageChanged', applyDirection);
  }, [i18n, location.pathname]);

  return (
    <ThemeProvider direction={direction}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div className="App">
          <CssBaseline />
          <ProtectedRoute>{content}</ProtectedRoute>
        </div>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

const CloseAction = (snackbarKey, closeSnackbar) => (
  <IconButton
    size="small"
    onClick={() => closeSnackbar(snackbarKey)}
    aria-label="close"
    sx={{ color: 'inherit' }}
  >
    <CloseIcon fontSize="small" />
  </IconButton>
);

export default function App() {
  return (
    <SnackbarProvider
      maxSnack={3}
      autoHideDuration={2500}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      preventDuplicate
      dense
      action={(key) => <SnackbarCloser snackbarKey={key} />}
    >
      <AppInner />
    </SnackbarProvider>
  );
}

function SnackbarCloser({ snackbarKey }) {
  const { closeSnackbar } = useSnackbar();
  return CloseAction(snackbarKey, closeSnackbar);
}
